// WerkvormenManagement.js - Updated: Steps array → Werkwijze text field + ID Management
import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { BackupComponent } from '../utils/database-backup-script';
import { MigrationComponent } from '../utils/werkvormen-migration';
import { IdFieldManager } from '../utils/idRenameScript';
import './WerkvormenManagement.css';

const WerkvormenManagement = () => {
  const [selectedTrede, setSelectedTrede] = useState('vertrouwen');
  const [werkvormen, setWerkvormen] = useState([]);
  const [editingWerkvorm, setEditingWerkvorm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('management');

  const tredes = [
    { id: 'vertrouwen', name: 'Vertrouwen' },
    { id: 'conflicten', name: 'Conflicten' },
    { id: 'betrokkenheid', name: 'Betrokkenheid' },
    { id: 'verantwoording', name: 'Verantwoording' },
    { id: 'resultaten', name: 'Resultaten' }
  ];

  useEffect(() => {
    loadWerkvormen();
  }, [selectedTrede]);

  const loadWerkvormen = async () => {
    setLoading(true);
    try {
      const tredeDoc = await getDoc(doc(db, 'tredes', selectedTrede));
      if (tredeDoc.exists()) {
        const data = tredeDoc.data();
        setWerkvormen(data.werkvormen || []);
      }
    } catch (error) {
      console.error('Error loading werkvormen:', error);
      alert('Fout bij laden werkvormen: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const saveWerkvormen = async (updatedWerkvormen) => {
    try {
      await updateDoc(doc(db, 'tredes', selectedTrede), {
        werkvormen: updatedWerkvormen,
        updatedAt: new Date().toISOString()
      });
      setWerkvormen(updatedWerkvormen);
      alert('✅ Werkvormen succesvol opgeslagen!');
    } catch (error) {
      console.error('Error saving werkvormen:', error);
      alert('⚠ Fout bij opslaan: ' + error.message);
    }
  };

  const handleEdit = (index) => {
    setEditingWerkvorm({ ...werkvormen[index], index });
  };

  const handleDelete = async (index) => {
    // eslint-disable-next-line no-restricted-globals
    if (confirm(`Weet je zeker dat je "${werkvormen[index].title}" wilt verwijderen?`)) {
      const updated = werkvormen.filter((_, i) => i !== index);
      await saveWerkvormen(updated);
    }
  };

  const handleSave = async (werkvormData) => {
    const updated = [...werkvormen];
    if (werkvormData.index !== undefined) {
      // Edit existing
      updated[werkvormData.index] = { ...werkvormData };
      delete updated[werkvormData.index].index;
    } else {
      // Add new
      updated.push({
        ...werkvormData,
        id: `${selectedTrede}_${Date.now()}`,
        createdAt: new Date().toISOString()
      });
    }
    await saveWerkvormen(updated);
    setEditingWerkvorm(null);
  };

  const handleAddNew = () => {
    setEditingWerkvorm({
      title: '',
      description: '',
      materials: [''],
      werkwijze: '', // UPDATED: werkwijze instead of steps
      tips: [''],
      variations: [''],
      debrief: ''
    });
  };

  const renderTabNavigation = () => (
    <div className="werkvormen-tabs">
      <div className="werkvormen-tabs-buttons">
        {[
          { id: 'management', label: '📝 Werkvormen Beheer' },
          { id: 'backup', label: '💾 Backup' },
          { id: 'migration', label: '🚀 Migratie' },
          { id: 'idmanagement', label: '🏷️ ID Management' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`werkvormen-tab-btn ${activeTab === tab.id ? 'werkvormen-tab-btn--active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderWerkvormenList = () => (
    <div>
      {/* Trede Selector */}
      <div className="trede-selector">
        <h3>Selecteer Trede:</h3>
        <div className="trede-buttons">
          {tredes.map(trede => (
            <button
              key={trede.id}
              onClick={() => setSelectedTrede(trede.id)}
              className={`trede-btn ${selectedTrede === trede.id ? 'trede-btn--active' : ''}`}
            >
              {trede.name}
            </button>
          ))}
        </div>
      </div>

      {/* Add New Button */}
      <div style={{ marginBottom: '20px', textAlign: 'right' }}>
        <button onClick={handleAddNew} className="add-werkvorm-btn">
          ➕ Nieuwe Werkvorm Toevoegen
        </button>
      </div>

      {/* Werkvormen List */}
      {loading ? (
        <div className="werkvormen-loading">
          <div>🔄 Werkvormen laden...</div>
        </div>
      ) : werkvormen.length === 0 ? (
        <div className="werkvormen-empty">
          <div>📝 Nog geen werkvormen voor {tredes.find(t => t.id === selectedTrede)?.name}</div>
          <button onClick={handleAddNew}>
            Eerste werkvorm toevoegen
          </button>
        </div>
      ) : (
        <div className="werkvormen-grid">
          {werkvormen.map((werkvorm, index) => (
            <div key={index} className="werkvorm-card">
              <div className="werkvorm-card-content">
                <div className="werkvorm-card-info">
                  <h4 className="werkvorm-card-title">
                    {werkvorm.title || 'Unnamed Werkvorm'}
                  </h4>
                  
                                
                {werkvorm.description && (
                    <p className="werkvorm-card-description">
                      {werkvorm.description}
                    </p>
                  )}
                  
                  <div className="werkvorm-card-stats">
                    {/* UPDATED: Show werkwijze length instead of steps count */}
                    {werkvorm.werkwijze ? 
                      `${werkvorm.werkwijze.length} characters werkwijze` : 
                      `${werkvorm.steps?.length || 0} steps (legacy)`
                    } • {werkvorm.tips?.length || 0} tips
                  </div>
                </div>
                
                <div className="werkvorm-card-actions">
                  <button
                    onClick={() => handleEdit(index)}
                    className="werkvorm-action-btn werkvorm-edit-btn"
                  >
                    ✏️ Bewerken
                  </button>
                  <button
                    onClick={() => handleDelete(index)}
                    className="werkvorm-action-btn werkvorm-delete-btn"
                  >
                    🗑️ Verwijderen
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderEditor = () => {
    if (!editingWerkvorm) return null;

    return (
      <WerkvormenEditor
        werkvorm={editingWerkvorm}
        onSave={handleSave}
        onCancel={() => setEditingWerkvorm(null)}
      />
    );
  };

  return (
    <div className="werkvormen-management">
      <h2>Werkvormen Management</h2>

      {renderTabNavigation()}

      {activeTab === 'management' && (
        editingWerkvorm ? renderEditor() : renderWerkvormenList()
      )}
      
      {activeTab === 'backup' && <BackupComponent />}
      {activeTab === 'migration' && <MigrationComponent />}
      {activeTab === 'idmanagement' && <IdFieldManager />}
    </div>
  );
};

// UPDATED: WerkvormenEditor Component met Werkwijze support
const WerkvormenEditor = ({ werkvorm, onSave, onCancel }) => {
  const [formData, setFormData] = useState(werkvorm);

  const handleArrayChange = (field, index, value) => {
    const updated = { ...formData };
    updated[field][index] = value;
    setFormData(updated);
  };

  const addArrayItem = (field) => {
    const updated = { ...formData };
    updated[field] = [...(updated[field] || []), ''];
    setFormData(updated);
  };

  const removeArrayItem = (field, index) => {
    const updated = { ...formData };
    updated[field] = updated[field].filter((_, i) => i !== index);
    setFormData(updated);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      alert('Titel is verplicht');
      return;
    }

    if (!formData.werkwijze.trim()) {
      alert('Werkwijze is verplicht');
      return;
    }
    
    // Clean up empty array items
    const cleanedData = { ...formData };
    ['materials', 'tips', 'variations'].forEach(field => {
      if (cleanedData[field]) {
        cleanedData[field] = cleanedData[field].filter(item => item.trim() !== '');
      }
    });
    
    onSave(cleanedData);
  };

  const renderArrayField = (field, label, placeholder) => (
    <div className="array-field-container">
      <label className="werkvorm-form-label">
        {label}:
      </label>
      {(formData[field] || []).map((item, index) => (
        <div key={index} className="array-field-item">
          <input
            type="text"
            value={item}
            onChange={(e) => handleArrayChange(field, index, e.target.value)}
            placeholder={placeholder}
            className="array-field-input"
          />
          <button
            type="button"
            onClick={() => removeArrayItem(field, index)}
            className="array-field-remove"
          >
            ❌
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addArrayItem(field)}
        className="array-field-add"
      >
        + {field === 'materials' ? 'Materiaal' : field === 'tips' ? 'Tip' : 'Variatie'} toevoegen
      </button>
    </div>
  );

  return (
    <div className="werkvorm-editor">
      <div className="werkvorm-editor-header">
        <h3 className="werkvorm-editor-title">
          {formData.index !== undefined ? 'Werkvorm Bewerken' : 'Nieuwe Werkvorm'}
        </h3>
        <button onClick={onCancel} className="werkvorm-editor-back-btn">
          ← Terug
        </button>
      </div>

      <form onSubmit={handleSubmit} className="werkvorm-form">
        {/* Basic Fields */}
        <div className="werkvorm-form-grid">
          <div className="werkvorm-form-group">
            <label className="werkvorm-form-label werkvorm-form-label--required">
              Titel:
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="werkvorm-form-input"
              placeholder="bijv. De Vertrouwens-Weersvoorspelling"
            />
          </div>              
        </div>

                <div className="werkvorm-form-group">
          <label className="werkvorm-form-label">
            Beschrijving:
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="werkvorm-form-textarea"
            placeholder="Korte uitleg van wat deze werkvorm doet en waarom het nuttig is..."
          />
        </div>

        {/* UPDATED: Werkwijze field - Main change! */}
        <div className="werkvorm-form-group werkvorm-form-group--full">
          <label className="werkvorm-form-label werkvorm-form-label--required">
            Werkwijze:
          </label>
          <textarea
            value={formData.werkwijze}
            onChange={(e) => setFormData({ ...formData, werkwijze: e.target.value })}
            rows={12}
            className="werkvorm-form-textarea werkwijze-textarea"
            placeholder={`Beschrijf de complete werkwijze hier. Bijvoorbeeld:

1. Teken een groot 'weerbericht' op het whiteboard met verschillende zones (vandaag, deze week, volgende maand)

2. Leg uit dat elk teamlid hun 'vertrouwensweer' kan aangeven: ☀️ volledig vertrouwen, ⛅ wisselvallig, 🌧️ stormen verwacht

3. Laat iedereen hun huidige vertrouwensniveau plaatsen en kort toelichten waarom

4. Bespreek wat er nodig is om meer 'zonnig weer' te krijgen

5. Maak concrete afspraken voor vertrouwensverbetering`}
            required
          />
          <div className="werkwijze-helper">
            💡 Tip: Gebruik genummerde stappen (1. 2. 3.) voor duidelijke structuur
          </div>
        </div>

        {/* Array Fields */}
        {renderArrayField('materials', 'Materialen', 'bijv. Whiteboard, markers, post-its')}
        {renderArrayField('tips', 'Tips', 'Voeg een tip toe')}
        {renderArrayField('variations', 'Variaties', 'Beschrijf een variatie')}

        <div className="werkvorm-form-group">
          <label className="werkvorm-form-label">
            Debrief vragen:
          </label>
          <textarea
            value={formData.debrief}
            onChange={(e) => setFormData({ ...formData, debrief: e.target.value })}
            rows={3}
            placeholder="Welke vragen help je het team om te reflecteren?"
            className="werkvorm-form-textarea"
          />
        </div>

        {/* Submit Buttons */}
        <div className="werkvorm-form-actions">
          <button type="button" onClick={onCancel} className="werkvorm-form-cancel">
            Annuleren
          </button>
          <button type="submit" className="werkvorm-form-submit">
            💾 Opslaan
          </button>
        </div>
      </form>
    </div>
  );
};

export default WerkvormenManagement;